import { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Typography,
} from '@mui/material';

import {
  StyledTableContainer,
  StyledTableHead,
  StyledTableBody,
  StyledTablePagination,
  StyledTableSortLabel,
  Table,
  TableCell,
  TableRow,
} from '@/components/StyledTable';

export interface FilterableColumn<T = any> {
  field: keyof T | string;
  label: string;
  width?: number;
  minWidth?: number;
  align?: 'left' | 'right' | 'center';
  filterable?: boolean;
  sortable?: boolean;
  render?: (value: any, row: T) => React.ReactNode;
  visible?: boolean;
}

export interface FilterableTableProps<T = any> {
  columns: FilterableColumn<T>[];
  data: T[];
  defaultRowsPerPage?: number;
  rowsPerPageOptions?: number[];
  defaultOrderBy?: string;
  defaultOrder?: 'asc' | 'desc';
  maxHeight?: number | string;
  stickyHeader?: boolean;
  getRowKey?: (row: T, index: number) => string | number;
  expandable?: boolean;
  renderExpandedRow?: (row: T) => React.ReactNode;
  onColumnVisibilityChange?: (columns: FilterableColumn<T>[]) => void;
}

function FilterableTable<T = any>({
  columns: initialColumns,
  data,
  defaultRowsPerPage = 25,
  rowsPerPageOptions = [10, 25, 50, 100],
  defaultOrderBy = '',
  defaultOrder = 'asc',
  maxHeight = 600,
  stickyHeader = true,
  getRowKey = (_, index) => index,
}: FilterableTableProps<T>) {
  // State
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [orderBy, setOrderBy] = useState<string>(defaultOrderBy);
  const [order, setOrder] = useState<'asc' | 'desc'>(defaultOrder);
  const [_expandedRows, _setExpandedRows] = useState<Set<string | number>>(new Set());
  const [columns, _setColumns] = useState<FilterableColumn<T>[]>(
    initialColumns.map(col => ({ ...col, visible: col.visible !== false }))
  );

  // Filter handler
  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page when filtering
  };

  // Sort handler
  const handleRequestSort = (field: string) => {
    const isAsc = orderBy === field && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(field);
  };

  // Apply filters
  const filteredData = useMemo(() => {
    return data.filter((row) => {
      return Object.entries(filters).every(([field, filterValue]) => {
        if (!filterValue) return true;
        const cellValue = String((row as any)[field] || '').toLowerCase();
        return cellValue.includes(filterValue.toLowerCase());
      });
    });
  }, [data, filters]);

  // Apply sorting
  const sortedData = useMemo(() => {
    if (!orderBy) return filteredData;

    const sorted = [...filteredData].sort((a, b) => {
      const aValue = (a as any)[orderBy];
      const bValue = (b as any)[orderBy];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Handle numeric values
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle string values
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      if (aStr < bStr) return order === 'asc' ? -1 : 1;
      if (aStr > bStr) return order === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredData, orderBy, order]);

  // Apply pagination
  const paginatedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        borderRadius: '10px',
        boxShadow: '0 10px 30px rgba(146,153,184,0.15)',
        overflow: 'hidden',
      }}
    >
      <StyledTableContainer sx={{ maxHeight }}>
        <Table stickyHeader={stickyHeader}>
          <StyledTableHead>
            {/* Header Row */}
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={String(column.field)}
                  align={column.align || 'left'}
                  sx={{
                    width: column.width,
                    minWidth: column.minWidth,
                  }}
                >
                  <Box>
                    {column.sortable !== false ? (
                      <StyledTableSortLabel
                        active={orderBy === column.field}
                        direction={orderBy === column.field ? order : 'asc'}
                        onClick={() => handleRequestSort(String(column.field))}
                      >
                        {column.label}
                      </StyledTableSortLabel>
                    ) : (
                      <Typography variant="body2" fontWeight={600}>
                        {column.label}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>

            {/* Filter Row */}
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={`filter-${String(column.field)}`}
                  align={column.align || 'left'}
                  sx={{ py: 1 }}
                >
                  {column.filterable !== false && (
                    <TextField
                      size="small"
                      placeholder={`Filter ${column.label}`}
                      value={filters[String(column.field)] || ''}
                      onChange={(e) =>
                        handleFilterChange(String(column.field), e.target.value)
                      }
                      sx={{
                        width: '100%',
                        '& .MuiInputBase-root': {
                          height: 32,
                          fontSize: '0.8125rem',
                          backgroundColor: '#F8F9FA',
                        },
                        '& .MuiInputBase-input': {
                          py: 0.75,
                        },
                      }}
                    />
                  )}
                </TableCell>
              ))}
            </TableRow>
          </StyledTableHead>

          <StyledTableBody>
            {paginatedData.map((row, index) => (
              <TableRow key={getRowKey(row, index)} hover>
                {columns.map((column) => {
                  const value = (row as any)[column.field];
                  return (
                    <TableCell
                      key={String(column.field)}
                      align={column.align || 'left'}
                    >
                      {column.render ? column.render(value, row) : (
                        <Typography variant="body2">
                          {value != null ? String(value) : ''}
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}

            {paginatedData.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No data found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </StyledTableBody>
        </Table>
      </StyledTableContainer>

      <StyledTablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        count={sortedData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </Box>
  );
}

export default FilterableTable;
